import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import RecommendationsList from './components/RecommendationsList/RecommendationsList'
import { AIRiskReportRenderModeProvider } from './components/RenderMode/AIRiskReportRenderModeProvider'
import SectionHeading from './components/SectionHeading/SectionHeading'
import { AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES, RECOMMENDATIONS_ANCHOR } from './data/constants'
import { AI_RISK_REPORT_RENDER_MODE_VALUES, DEFAULT_AI_RISK_REPORT_RENDER_MODE } from './data/renderMode'
import { buildAllRecommendations } from './data/recommendations'
import DataExposure from './sections/DataExposure/DataExposure'
import DeploymentReadiness from './sections/DeploymentReadiness/DeploymentReadiness'
import IdentityAccess from './sections/IdentityAccess/IdentityAccess'
import Overview from './sections/Overview/Overview'
import Reference from './sections/Reference/Reference'
import ThirdPartyAi from './sections/ThirdPartyAi/ThirdPartyAi'
import styles from './AIRiskReport.module.scss'

export default function AIRiskReportRenderer({
  report,
  conditionalAccessPolicies = [],
  conditionalAccessPoliciesIsLoading = false,
  conditionalAccessPoliciesIsError = false,
  renderMode = DEFAULT_AI_RISK_REPORT_RENDER_MODE,
}) {
  const intl = useIntl()
  const {
    assessment = {},
    summary = {},
    domainScores = [],
    findings = [],
    quickWins = [],
    provenanceSummary = {},
    dataAvailability = [],
    data = {},
    meta = {},
  } = report
  const recommendations = useMemo(
    () => buildAllRecommendations({ intl, quickWins, findings }),
    [intl, quickWins, findings],
  )

  return (
    <AIRiskReportRenderModeProvider renderMode={renderMode}>
      <div className={`${styles.page} w-100 mx-auto`}>
        <div {...AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES}>
          <Overview
            assessment={assessment}
            summary={summary}
            recommendations={recommendations}
            findings={findings}
            domainScores={domainScores}
            data={data}
            dataAvailability={dataAvailability}
          />
        </div>

        <DeploymentReadiness summary={summary} data={data} dataAvailability={dataAvailability} />
        <DataExposure summary={summary} data={data} domainScores={domainScores} dataAvailability={dataAvailability} />
        <IdentityAccess
          summary={summary}
          data={data}
          domainScores={domainScores}
          dataAvailability={dataAvailability}
          conditionalAccessPolicies={conditionalAccessPolicies}
          conditionalAccessPoliciesIsLoading={conditionalAccessPoliciesIsLoading}
          conditionalAccessPoliciesIsError={conditionalAccessPoliciesIsError}
        />
        <ThirdPartyAi
          summary={summary}
          data={data}
          domainScores={domainScores}
          findings={findings}
          dataAvailability={dataAvailability}
        />

        <div {...AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES}>
          <SectionHeading id={RECOMMENDATIONS_ANCHOR} titleId="AI_RISK_REPORT.RECOMMENDATIONS" keepWithNext />
          <RecommendationsList items={recommendations} />
          <Reference meta={meta} provenanceSummary={provenanceSummary} />
        </div>
      </div>
    </AIRiskReportRenderModeProvider>
  )
}

const reportPropType = PropTypes.shape({
  assessment: PropTypes.shape({}),
  summary: PropTypes.shape({}),
  domainScores: PropTypes.arrayOf(PropTypes.shape({})),
  findings: PropTypes.arrayOf(PropTypes.shape({})),
  quickWins: PropTypes.arrayOf(PropTypes.shape({})),
  provenanceSummary: PropTypes.shape({}),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
  data: PropTypes.shape({}),
  meta: PropTypes.shape({}),
})

AIRiskReportRenderer.propTypes = {
  report: reportPropType.isRequired,
  conditionalAccessPolicies: PropTypes.arrayOf(PropTypes.shape({})),
  conditionalAccessPoliciesIsLoading: PropTypes.bool,
  conditionalAccessPoliciesIsError: PropTypes.bool,
  renderMode: PropTypes.oneOf(AI_RISK_REPORT_RENDER_MODE_VALUES),
}
